import React, { useEffect, useRef } from 'react'
import { Trans } from '@lingui/macro'
import queryString from 'query-string'
import { Link, useHistory, useLocation, useParams } from 'react-router-dom'

import {
  Exact,
  FollowResourceType,
  PathwayEnrolmentStatusEnum,
  PathwayNotificationMessageEnum,
  PathwayProgress
} from 'front-end/src/graphql/private/PrivateSchemaTypes'

import HeadTitle from '../../components/common/head/HeadTitle'
import IconBan from '../../components/common/icon/IconBan'
import Loader from '../../components/common/loader/Loader'
import PathwayProgressDetail from './components/pathway-progress-detail/PathwayProgressDetail'
import Tag from '../../components/common/tag/Tag'
import { CommentsFooter } from '../../components/common/comments/comments-footer/CommentsFooter'
import { linkBuilder, staticLinks } from 'shared/linkBuilder'
import { Page } from '../../components/common/page/Page'
import { PathwayTopicCards } from './PathwayTopicCards'
import { scormStore } from '../../app/stores/SCORMStore'
import { getScheduledActionIdFromURL } from '../../utils/campaignTracking'
import {
  PathwayDetailPageQuery,
  usePathwayDetailPageLazyQuery,
  usePathwayOpenedFromCampaignShareEmailMutation
} from './PathwayDetailPage.codegen'

import './PathwayDetailPage.less'
import dayjs from 'shared/dayjs'
import { notification } from 'front-end/src/components/common/notifications/Notifications'
import { QueryHookOptions } from '@apolloHooksWithThrowingQuery'
import { PathwayDetailTopbar } from './components/PathwayDetailTopbar'
import { Box, Container, Heading, Flex, Alert } from 'front-end/src/components'
import { PathwayByline } from './components/PathwayByline'
import { brandColors } from 'shared/basicPalette'
import { DangerouslySetInnerHTML } from '../../utils/DangerouslySetInnerHTML'
import { PathwayDetailPageContext } from './PathwayDetailPageContext'
import { getQuillStyle } from 'front-end/src/components/common/quill/EditorQuill'

const notificationMessages = {
  [PathwayNotificationMessageEnum.P1]: (
    <Trans>
      This pathway has been updated since your last visit. Your progress was
      updated.
    </Trans>
  ),
  [PathwayNotificationMessageEnum.P2]: (
    <Trans>
      This pathway has been updated since your last visit. Your progress was not
      affected.
    </Trans>
  ),
  [PathwayNotificationMessageEnum.P3]: (
    <Trans>
      This pathway has been shared with you. Your progress was updated.
    </Trans>
  ),
  [PathwayNotificationMessageEnum.P4]: (
    <Trans>
      A new assignment has been created and your progress was reset.
    </Trans>
  )
}

function usePathwayDetailPageQueryWithParams(
  queryParams?:
    | QueryHookOptions<
        PathwayDetailPageQuery,
        Exact<{ id: number; specificVersion: boolean; version?: string }>
      >
    | undefined
) {
  const { pathwayId, pathwayVersion } =
    useParams<{ pathwayId: string; pathwayVersion?: string }>()

  const [refetch, { loading, data }] = usePathwayDetailPageLazyQuery({
    variables: {
      id: Number(pathwayId),
      specificVersion: !!pathwayVersion,
      version: pathwayVersion
    },
    ...queryParams
  })
  return { refetch, data, loading }
}

export const PathwayDetailPage = () => {
  const history = useHistory()
  const { search } = useLocation()
  const { pathwayId, pathwayVersion } =
    useParams<{ pathwayId: string; pathwayVersion?: string }>()

  const [openedFromCampaign] = usePathwayOpenedFromCampaignShareEmailMutation()

  const {
    data,
    loading,
    refetch: refetchPathway
  } = usePathwayDetailPageQueryWithParams()
  useEffect(() => {
    refetchPathway() // make sure, pathway will be fetched each time, these components mounts
  }, [])

  useEffect(() => {
    // Campaigns links click tracking
    const parsedQueryString = queryString.parse(search)
    const scheduledActionId = getScheduledActionIdFromURL(parsedQueryString)

    if (scheduledActionId) {
      // This pathway was opened from Pathway share action email sent from campaign.
      openedFromCampaign({
        variables: {
          id: Number(pathwayId),
          scheduledActionId
        }
      })

      // Remove scheduled_action param from current history state (means from current URL in browser)
      delete parsedQueryString.scheduled_action
      history.replace(
        `${linkBuilder.pathwayDetail(
          Number(pathwayId)
        )}?${queryString.stringify(parsedQueryString)}`
      )
    }
  }, [pathwayId])

  const isInitialMount = useRef(true)
  const isSCORMUpdateInProgress = scormStore.isUpdateInProgress

  // If there was SCORM sync in progress we have to refetch pathway progress.
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false
    } else {
      refetchPathway()
    }
  }, [isSCORMUpdateInProgress])

  if (!data || loading || isSCORMUpdateInProgress) {
    return <Loader />
  }

  const notificationMessage =
    data.pathway?.currentEnrolment?.notificationMessage

  if (notificationMessage && notificationMessages[notificationMessage]) {
    notification.warning(notificationMessages[notificationMessage])
  }

  const { pathway } = data

  if (!pathway) {
    return (
      <>
        <Box className="Auth__noAccess">
          <IconBan />
          <h2>
            <Trans>You don't have access to this pathway</Trans>
          </h2>
          <p>
            <Trans>
              Go back <Link to={staticLinks.home}>home</Link>
            </Trans>
          </p>
        </Box>
      </>
    )
  }

  const completedAt =
    pathway.currentEnrolment &&
    dayjs(pathway.currentEnrolment.completed_at).utc(false)

  const isFailed =
    pathway.currentEnrolment?.status === PathwayEnrolmentStatusEnum.FAILED

  const isChanged =
    pathway.currentEnrolment?.pathwayVersion?.id !==
    pathway.production_pathway_version_id

  const showChangesMessage = completedAt && !isFailed && isChanged

  const showFailedNoLockMessage =
    completedAt && isFailed && pathway.progress === PathwayProgress.open

  const showFailedEnforcedMessage =
    completedAt && isFailed && pathway.progress === PathwayProgress.linear

  const showFailedLockedMessage =
    completedAt && isFailed && pathway.progress === PathwayProgress.locked

  const version = pathwayVersion
    ? pathway.specificVersion && pathway.specificVersion[0]
    : pathway.latestVersion && pathway.latestVersion[0]

  return (
    <PathwayDetailPageContext.Provider
      value={{ refetch: refetchPathway, pathway }}
    >
      <PathwayDetailTopbar title={pathway.title} />
      <Page withTopbar className="PathwayDetailPage">
        <HeadTitle title={pathway.title} />
        <Container maxW="container.md" px="30px">
          <Flex justify="space-between" direction={['column', 'row']}>
            <Box w={['100%', '66%']}>
              <Heading mb="12px" size="xl" className="applyCustomFont">
                {pathway.title}
              </Heading>
              <PathwayByline
                version={version?.version as string}
                publishedDate={pathway.published_date}
                updatedDate={pathway.updated_at}
                resourcesCount={version?.topics?.length || 0}
                isArchived={
                  version?.id !== pathway.production_pathway_version_id
                }
              />

              <Box
                // because of images
                className="page-content"
                mt="12px"
                color={brandColors.grey.mid}
                fontSize={'xl'}
              >
                <Box css={getQuillStyle('ql-editor')} className="ql-editor">
                  <DangerouslySetInnerHTML
                    content={pathway.description || ''}
                  />
                </Box>
              </Box>
            </Box>

            {!pathwayVersion && (
              <Box w={['100%', '31%']}>
                <PathwayProgressDetail
                  showVersion={
                    version?.id !== pathway.currentEnrolment?.pathwayVersion?.id
                  }
                  /* @ts-ignore */
                  enrolment={pathway.currentEnrolment}
                />

                <Box mt={['8px', '32px']}>
                  {showChangesMessage && (
                    <Alert status="success">
                      <Trans>
                        Since you have completed this pathway, changes were made
                        to it or its resources. You may complete additional
                        resources but your enrolment record will not be affected
                        until a new enrolment is created. Please contact an
                        administrator.
                      </Trans>
                    </Alert>
                  )}
                  {showFailedNoLockMessage && (
                    <Alert status="warning">
                      <Trans>
                        You have failed this pathway as you failed to pass a
                        quiz. You can still progress with other resources.
                        Please contact an administrator.
                      </Trans>
                    </Alert>
                  )}
                  {showFailedEnforcedMessage && (
                    <Alert status="warning">
                      <Trans>
                        You have failed this pathway as you failed to pass a
                        quiz. You may progress with other resources but this
                        will not affect your current pathway record until a new
                        enrolment is created. Please contact an administrator.
                      </Trans>
                    </Alert>
                  )}
                  {showFailedLockedMessage && (
                    <Alert status="error">
                      <Trans>
                        You have failed this pathway as you failed to pass a
                        quiz. This pathway is locked until a new enrolment is
                        created. Please contact an administrator.
                      </Trans>
                    </Alert>
                  )}
                </Box>
              </Box>
            )}
          </Flex>
        </Container>
        <Box>
          <Box className="PathwayDetailPage__bodyInner">
            <Container maxW="calc(100vw/2 + 498px)" mr="0" ml="auto" p="0">
              <PathwayTopicCards />
            </Container>

            <Container maxW="container.md" px="30px">
              {pathway.tags.length > 0 && (
                <Box className="PathwayDetailPage__TagsList">
                  <Trans>Tags</Trans>:
                  {pathway.tags.map((tag) => (
                    <Tag key={tag.id} size="normal" color="clear">
                      {tag.name}
                    </Tag>
                  ))}
                </Box>
              )}
            </Container>
          </Box>
        </Box>
        {!pathwayVersion && (
          <Box className="PathwayDetailPage__CommentsFooter">
            <CommentsFooter
              itemType={FollowResourceType.pathway}
              itemId={pathway.id}
              allowComments={pathway.allow_comments}
            />
          </Box>
        )}
      </Page>
    </PathwayDetailPageContext.Provider>
  )
}
